import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ExternalLink, Map } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getPublicHelpUrl } from '@/lib/public-site'
import { ProductTourStepIcon } from '@/components/layout/product-tour-step-icon'
import {
  ProductTourAnatomyDiagram,
  productTourRegionTestId,
  type ProductTourShellRegionId,
} from '@/components/layout/product-tour-anatomy'
import {
  getProductTourTargetRect,
  isProductTourTargetPresent,
  placeProductTourTooltip,
  productTourRectTravel,
  PRODUCT_TOUR_ACTIVE_ATTR,
  PRODUCT_TOUR_START_EVENT,
  PRODUCT_TOUR_STEPS,
  getProductTourPeekRect,
  setProductTourPeekOpen,
  setProductTourWorkspaceScopeOpen,
  type ProductTourRect,
  type ProductTourStep,
} from '@/lib/product-tour'
import { StorageKeys } from '@/lib/storage/keys'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { StorageSchemas } from '@/lib/storage/schemas'
import { openSidebarMode } from '@/lib/sidebar-mode'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

const TOOLTIP_WIDTH = 360
const TOOLTIP_WIDTH_ANATOMY = 440
/** Soft card glide for short nudges only */
const TOUR_MOTION_MS = 520
/** Treat as a local nudge (animate card) below this travel distance */
const TOUR_LOCAL_TRAVEL_PX = 100
/** Stable dim via box-shadow - never fade this between steps (avoids white flash).
 * 0.72 so a dark theme still recedes; 0.38 left the chrome fully readable. */
const TOUR_DIM_SHADOW = '0 0 0 9999px rgba(0,0,0,0.72)'
/** Welcome-map holes crossfade instead of sliding. */
const TOUR_HOLE_FADE_MS = 360

type TourHoleLayer = {
  id: number
  rect: ProductTourRect
  leaving: boolean
}

function tourHolesNearlySame(a: ProductTourRect, b: ProductTourRect): boolean {
  return (
    Math.abs(a.top - b.top) < 24 &&
    Math.abs(a.left - b.left) < 24 &&
    Math.abs(a.width - b.width) < 48 &&
    Math.abs(a.height - b.height) < 48
  )
}

/** Zero-size hole = full dim, no bright cutout */
const TOUR_FULL_DIM_HOLE: ProductTourRect = { top: 0, left: 0, width: 0, height: 0 }

type ProductTourHostProps = {
  /** Expand left sidebar (show + uncollapse). */
  ensureLeftSidebarVisible: () => void
  /** Expand agent panel (show + uncollapse). */
  ensureAgentPanelVisible: () => void
}

function applyStepAction(step: ProductTourStep | undefined) {
  if (!step?.action) return
  if (step.action === 'open-workspace-scope') {
    setProductTourWorkspaceScopeOpen(true)
  } else if (step.action === 'close-workspace-scope') {
    setProductTourWorkspaceScopeOpen(false)
  }
}

export function ProductTourHost({
  ensureLeftSidebarVisible,
  ensureAgentPanelVisible,
}: ProductTourHostProps) {
  const [completed, setCompleted] = usePersistedState({
    key: StorageKeys.PRODUCT_TOUR_COMPLETED,
    defaultValue: false,
    schema: StorageSchemas.boolean,
  })
  const [promptOpen, setPromptOpen] = useState(false)
  const [tourActive, setTourActive] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [hole, setHole] = useState<ProductTourRect | null>(null)
  const tooltipHoleRef = useRef<ProductTourRect | null>(null)
  const liveHoleRef = useRef<ProductTourRect | null>(null)
  const holeLayerIdRef = useRef(0)
  const [holeLayers, setHoleLayers] = useState<TourHoleLayer[]>([])
  const [anatomyActiveId, setAnatomyActiveId] = useState<ProductTourShellRegionId | null>(null)
  const anatomyLeaveTimerRef = useRef<number | null>(null)
  const nextButtonRef = useRef<HTMLButtonElement>(null)
  const promptShownRef = useRef(false)

  const steps = PRODUCT_TOUR_STEPS
  const step = steps[stepIndex] ?? steps[0]
  const isLast = stepIndex >= steps.length - 1
  const isFirst = stepIndex <= 0

  const prepareChrome = useCallback(() => {
    ensureLeftSidebarVisible()
    ensureAgentPanelVisible()
    openSidebarMode('browse-nav')
  }, [ensureAgentPanelVisible, ensureLeftSidebarVisible])

  const finishTour = useCallback(() => {
    if (anatomyLeaveTimerRef.current) {
      window.clearTimeout(anatomyLeaveTimerRef.current)
      anatomyLeaveTimerRef.current = null
    }
    setProductTourWorkspaceScopeOpen(false)
    setProductTourPeekOpen(false)
    setTourActive(false)
    setStepIndex(0)
    setHole(null)
    tooltipHoleRef.current = null
    setAnatomyActiveId(null)
    setCompleted(true)
  }, [setCompleted])

  const handleAnatomyActiveChange = useCallback((id: ProductTourShellRegionId | null) => {
    if (anatomyLeaveTimerRef.current) {
      window.clearTimeout(anatomyLeaveTimerRef.current)
      anatomyLeaveTimerRef.current = null
    }
    if (id === 'peek') {
      setProductTourPeekOpen(true)
      setAnatomyActiveId(id)
      return
    }
    setProductTourPeekOpen(false)
    if (id) {
      setAnatomyActiveId(id)
      return
    }
    // Debounce clear so moving between adjacent map regions does not flash full dim
    anatomyLeaveTimerRef.current = window.setTimeout(() => {
      setAnatomyActiveId(null)
      anatomyLeaveTimerRef.current = null
    }, 120)
  }, [])

  const startTour = useCallback(() => {
    setPromptOpen(false)
    prepareChrome()
    setStepIndex(0)
    setTourActive(true)
    // Let layout expand before measuring / opening workspace scope
    window.setTimeout(() => {
      applyStepAction(PRODUCT_TOUR_STEPS[0])
      nextButtonRef.current?.focus()
    }, 80)
  }, [prepareChrome])

  // Raise Radix menus above the tour dim; clear on exit
  useEffect(() => {
    if (!tourActive) {
      document.documentElement.removeAttribute(PRODUCT_TOUR_ACTIVE_ATTR)
      return
    }
    document.documentElement.setAttribute(PRODUCT_TOUR_ACTIVE_ATTR, '')
    return () => document.documentElement.removeAttribute(PRODUCT_TOUR_ACTIVE_ATTR)
  }, [tourActive])

  // First-login soft prompt (once per browser until Skip or Done)
  useEffect(() => {
    if (completed || promptShownRef.current || tourActive) return
    promptShownRef.current = true
    const t = window.setTimeout(() => setPromptOpen(true), 600)
    return () => window.clearTimeout(t)
  }, [completed, tourActive])

  // Help menu / external replay
  useEffect(() => {
    const onStart = () => {
      setPromptOpen(false)
      startTour()
    }
    window.addEventListener(PRODUCT_TOUR_START_EVENT, onStart)
    return () => window.removeEventListener(PRODUCT_TOUR_START_EVENT, onStart)
  }, [startTour])

  // Resolve skippable steps when advancing (after chrome preconditions applied)
  const resolveIndex = useCallback((from: number, direction: 1 | -1): number => {
    let i = from
    while (i >= 0 && i < steps.length) {
      const candidate = steps[i]
      if (
        candidate.skipIfMissing &&
        candidate.testId &&
        !isProductTourTargetPresent(candidate.testId)
      ) {
        i += direction
        continue
      }
      return i
    }
    return direction === 1 ? steps.length - 1 : 0
  }, [steps])

  const goTo = useCallback(
    (next: number) => {
      const direction: 1 | -1 = next >= stepIndex ? 1 : -1
      prepareChrome()
      // Expand chrome first, then resolve skip-if-missing against the live DOM
      window.setTimeout(() => {
        const resolved = resolveIndex(next, direction)
        const nextStep = steps[resolved]
        const openingScope = nextStep?.action === 'open-workspace-scope'
        // Close flyout unless the next step needs it (avoids open/close flicker)
        if (!openingScope) setProductTourWorkspaceScopeOpen(false)
        setProductTourPeekOpen(false)
        setAnatomyActiveId(null)
        setStepIndex(resolved)
        window.setTimeout(() => {
          applyStepAction(nextStep)
          nextButtonRef.current?.focus()
        }, openingScope ? 140 : 40)
      }, 80)
    },
    [prepareChrome, resolveIndex, stepIndex, steps],
  )

  const goNext = useCallback(() => {
    if (isLast) {
      finishTour()
      return
    }
    goTo(stepIndex + 1)
  }, [finishTour, goTo, isLast, stepIndex])

  const goBack = useCallback(() => {
    if (isFirst) return
    goTo(stepIndex - 1)
  }, [goTo, isFirst, stepIndex])

  // Click spotlight target to advance (workspace picker -> open list)
  useEffect(() => {
    if (!tourActive || !step.advanceOnTargetActivate || !step.testId) return
    let armed = true
    const onPointerDown = (e: PointerEvent) => {
      if (!armed) return
      const el = e.target
      if (!(el instanceof Element)) return
      if (!el.closest(`[data-testid="${step.testId}"]`)) return
      armed = false
      // Let the click open the control, then advance
      window.setTimeout(() => {
        goNext()
      }, 30)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [goNext, step.advanceOnTargetActivate, step.testId, stepIndex, tourActive])

  // Spotlight measurement (step target, open flyouts, or anatomy hover -> live chrome)
  useLayoutEffect(() => {
    if (!tourActive) return

    const measure = () => {
      if (step.showAnatomy && anatomyActiveId === 'peek') {
        const peek = getProductTourPeekRect()
        if (peek) setHole(peek)
        return
      }
      if (step.showAnatomy && anatomyActiveId) {
        const liveId = productTourRegionTestId(anatomyActiveId)
        setHole(getProductTourTargetRect(liveId || null, 0))
        return
      }
      if (step.showAnatomy && !anatomyActiveId) {
        setHole(null)
        return
      }
      const preferExpand = Boolean(step.expandTestIds?.length)
      setHole(
        getProductTourTargetRect(step.testId, 0, step.expandTestIds ?? [], {
          preferExpand,
        }),
      )
    }
    measure()
    // Flyouts zoom in - remeasure after open animation settles
    const delays = step.expandTestIds?.length
      ? [80, 200, 360, 520]
      : anatomyActiveId === 'peek'
        ? [40, 160, 360]
        : [100]
    const timers = delays.map((ms) => window.setTimeout(measure, ms))
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    const watchFlyouts = Boolean(step.expandTestIds?.length)
    let mo: MutationObserver | null = null
    let moDebounce: number | null = null
    if (watchFlyouts) {
      mo = new MutationObserver(() => {
        if (moDebounce != null) window.clearTimeout(moDebounce)
        moDebounce = window.setTimeout(measure, 60)
      })
      mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state', 'style'] })
    }
    return () => {
      timers.forEach((t) => window.clearTimeout(t))
      if (moDebounce != null) window.clearTimeout(moDebounce)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
      mo?.disconnect()
    }
  }, [anatomyActiveId, tourActive, step.expandTestIds, step.showAnatomy, step.testId, stepIndex])

  // Esc = skip; arrows = step
  useEffect(() => {
    if (!tourActive) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        finishTour()
        return
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        goNext()
        return
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        goBack()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [finishTour, goBack, goNext, tourActive])

  // Park card beside the *current* step target (not lagged spotlight geometry).
  // Long jumps snap - CSS top/left transition was sliding Search→Account across the shell.
  const tooltipWidth = step.showAnatomy ? TOOLTIP_WIDTH_ANATOMY : TOOLTIP_WIDTH
  const tooltipPos = useMemo(() => {
    if (step.showAnatomy) {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800
      const cardH = 420
      return {
        top: Math.max(24, Math.min(vh - cardH - 24, vh * 0.42)),
        left: Math.max(16, (vw - tooltipWidth) / 2),
      }
    }
    return placeProductTourTooltip(
      hole,
      step.tooltipBias,
      tooltipWidth,
      260,
    )
  }, [hole, step.showAnatomy, step.tooltipBias, tooltipWidth])

  // Decide motion for THIS render before paint (state would lag one frame and allow a slide)
  const tooltipMotionEnabled = (() => {
    if (step.showAnatomy || !hole) return false
    const prev = tooltipHoleRef.current
    if (!prev) return false
    return productTourRectTravel(prev, hole) <= TOUR_LOCAL_TRAVEL_PX
  })()

  useLayoutEffect(() => {
    tooltipHoleRef.current = hole
  }, [hole])

  // Anatomy hovers crossfade holes. A geometry slide reads as the window stretching
  // when header, main, and the peek are very different shapes.
  useLayoutEffect(() => {
    if (!step.showAnatomy) {
      liveHoleRef.current = null
      setHoleLayers([])
      return
    }
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const prev = liveHoleRef.current
    const nextHole = hole && hole.width > 0 && hole.height > 0 ? hole : null
    if (!nextHole) {
      liveHoleRef.current = null
      setHoleLayers((layers) => {
        if (!layers.some((layer) => !layer.leaving)) return layers
        if (reduceMotion) return []
        return layers.map((layer) => ({ ...layer, leaving: true }))
      })
      return
    }
    if (prev && tourHolesNearlySame(prev, nextHole)) {
      liveHoleRef.current = nextHole
      setHoleLayers((layers) => {
        const live = layers.find((layer) => !layer.leaving)
        if (live && tourHolesNearlySame(live.rect, nextHole)) return layers
        return layers.map((layer) => (layer.leaving ? layer : { ...layer, rect: nextHole }))
      })
      return
    }
    liveHoleRef.current = nextHole
    const id = ++holeLayerIdRef.current
    setHoleLayers((layers) => {
      if (reduceMotion) return [{ id, rect: nextHole, leaving: false }]
      return [
        ...layers.map((layer) => ({ ...layer, leaving: true })),
        { id, rect: nextHole, leaving: false },
      ]
    })
  }, [hole, step.showAnatomy])

  const skipPrompt = () => {
    setPromptOpen(false)
    setCompleted(true)
  }

  // One stable dim layer for the whole tour - only the cutout rect moves (never fades to clear)
  const spot = hole ?? TOUR_FULL_DIM_HOLE
  const hasCutout = Boolean(hole && hole.width > 0 && hole.height > 0)
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 1
  const motionEase = 'cubic-bezier(0.22, 1, 0.36, 1)'
  const tooltipMotionStyle = tooltipMotionEnabled
    ? {
        transitionDuration: `${TOUR_MOTION_MS}ms`,
        transitionTimingFunction: motionEase,
      }
    : { transitionDuration: '0ms', transitionTimingFunction: motionEase }
  const cutoutGeomStyle = {
    transitionProperty: 'top, left, width, height',
    transitionDuration: '0ms',
  }

  const overlay = tourActive && typeof document !== 'undefined'
    ? createPortal(
        <div
          className="fixed inset-0 z-[100] pointer-events-none"
          data-testid={testIds.productTour.root}
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-tour-title"
        >
          {/* Radix menus default to z-50; lift them above the tour dim while active */}
          <style>{`
            html[${PRODUCT_TOUR_ACTIVE_ATTR}] [data-radix-popper-content-wrapper] {
              z-index: 120 !important;
            }
            html[${PRODUCT_TOUR_ACTIVE_ATTR}] [data-radix-menu-content],
            html[${PRODUCT_TOUR_ACTIVE_ATTR}] [role="menu"] {
              z-index: 120 !important;
            }
            /* Tour: keep extension + visible even with no extensions / no hover */
            html[${PRODUCT_TOUR_ACTIVE_ATTR}] [data-testid="${testIds.leftSidebar.railAddExtension}"] {
              opacity: 1 !important;
            }
            .product-tour-cutout-pulse {
              outline: 2px solid hsl(var(--primary) / 0.55);
              outline-offset: 0;
              animation: product-tour-cutout-pulse 1.45s ease-out forwards;
            }
            @keyframes product-tour-cutout-pulse {
              0% {
                outline-color: hsl(var(--primary) / 0);
                box-shadow: 0 0 0 0 hsl(var(--primary) / 0);
              }
              18% {
                outline-color: hsl(var(--primary) / 1);
                box-shadow: 0 0 0 4px hsl(var(--primary) / 0.35);
              }
              36% {
                outline-color: hsl(var(--primary) / 0.25);
                box-shadow: 0 0 0 0 hsl(var(--primary) / 0);
              }
              54% {
                outline-color: hsl(var(--primary) / 0.95);
                box-shadow: 0 0 0 3px hsl(var(--primary) / 0.28);
              }
              100% {
                outline-color: hsl(var(--primary) / 0.55);
                box-shadow: 0 0 0 0 hsl(var(--primary) / 0);
              }
            }
            .product-tour-hole-in {
              animation: product-tour-hole-in ${TOUR_HOLE_FADE_MS}ms ease both;
            }
            .product-tour-hole-out {
              animation: product-tour-hole-out ${TOUR_HOLE_FADE_MS}ms ease forwards;
            }
            @keyframes product-tour-hole-in {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes product-tour-hole-out {
              from { opacity: 1; }
              to { opacity: 0; }
            }
            @media (prefers-reduced-motion: reduce) {
              .product-tour-cutout-pulse {
                animation: none;
                outline-color: hsl(var(--primary) / 0.7);
                box-shadow: none;
              }
              .product-tour-hole-in,
              .product-tour-hole-out {
                animation: none;
              }
              .product-tour-hole-out { opacity: 0; }
            }
          `}</style>
          <div data-testid={testIds.productTour.overlay} className="absolute inset-0 pointer-events-none" aria-hidden>
            {/* Steps: one snapped cutout. Welcome map: old and new holes fade together. */}
            {step.showAnatomy ? (
              <svg
                className="absolute inset-0 h-full w-full pointer-events-none"
                width={viewportW}
                height={viewportH}
                aria-hidden
              >
                <defs>
                  <mask
                    id="product-tour-hole-mask"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width={viewportW}
                    height={viewportH}
                  >
                    <rect x="0" y="0" width={viewportW} height={viewportH} fill="white" />
                    {holeLayers.map((layer) => (
                      <rect
                        key={layer.id}
                        x={layer.rect.left}
                        y={layer.rect.top}
                        width={layer.rect.width}
                        height={layer.rect.height}
                        fill="black"
                        className={layer.leaving ? 'product-tour-hole-out' : 'product-tour-hole-in'}
                        onAnimationEnd={(event) => {
                          if (!layer.leaving) return
                          if (event.target !== event.currentTarget) return
                          setHoleLayers((layers) => layers.filter((item) => item.id !== layer.id))
                        }}
                      />
                    ))}
                  </mask>
                </defs>
                <rect
                  x="0"
                  y="0"
                  width={viewportW}
                  height={viewportH}
                  fill="rgba(0,0,0,0.72)"
                  mask="url(#product-tour-hole-mask)"
                />
              </svg>
            ) : (
              <div
                className="absolute pointer-events-none"
                style={{
                  top: spot.top,
                  left: spot.left,
                  width: spot.width,
                  height: spot.height,
                  boxShadow: TOUR_DIM_SHADOW,
                  ...cutoutGeomStyle,
                }}
              />
            )}
            {hasCutout && !step.showAnatomy ? (
              <div
                key={step.id}
                className="product-tour-cutout-pulse absolute pointer-events-none"
                style={{
                  top: spot.top,
                  left: spot.left,
                  width: spot.width,
                  height: spot.height,
                }}
              />
            ) : null}
            {/* Click shields - full catch when no cutout; ring around hole when cutout is on */}
            {!hasCutout ? (
              <div className="absolute inset-0 bg-transparent pointer-events-auto" />
            ) : (
              <>
                <div
                  className="absolute left-0 right-0 top-0 bg-transparent pointer-events-auto"
                  style={{ height: spot.top }}
                />
                <div
                  className="absolute left-0 bg-transparent pointer-events-auto"
                  style={{ top: spot.top, width: spot.left, height: spot.height }}
                />
                <div
                  className="absolute right-0 bg-transparent pointer-events-auto"
                  style={{
                    top: spot.top,
                    left: spot.left + spot.width,
                    height: spot.height,
                  }}
                />
                <div
                  className="absolute bottom-0 left-0 right-0 bg-transparent pointer-events-auto"
                  style={{ top: spot.top + spot.height }}
                />
              </>
            )}
          </div>

          <div
            data-testid={testIds.productTour.tooltip}
            className={cn(
              'fixed z-[101] rounded-lg border border-border bg-popover p-5 text-popover-foreground shadow-lg pointer-events-auto',
              'transition-[top,left]',
              step.showAnatomy ? 'w-[440px]' : 'w-[360px]',
            )}
            style={{ top: tooltipPos.top, left: tooltipPos.left, ...tooltipMotionStyle }}
          >
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
              Step {stepIndex + 1} of {steps.length}
            </p>
            {!step.showAnatomy ? <ProductTourStepIcon stepId={step.id} /> : null}
            <h2 id="product-tour-title" className="text-lg font-semibold mb-2">
              {step.title}
            </h2>
            <p className={cn('text-base text-muted-foreground leading-snug', step.showAnatomy ? 'mb-3' : 'mb-5')}>
              {step.body}
            </p>
            {step.showAnatomy ? (
              <div className="mb-5">
                <ProductTourAnatomyDiagram
                  activeId={anatomyActiveId}
                  onActiveChange={handleAnatomyActiveChange}
                />
              </div>
            ) : null}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="ghost"
                size="default"
                onClick={finishTour}
                data-testid={testIds.productTour.skipButton}
              >
                Skip
              </Button>
              <div className="flex-1" />
              <span className="text-[11px] text-muted-foreground/80 tabular-nums hidden sm:inline mr-1">
                ← →
              </span>
              {!isFirst ? (
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={goBack}
                  data-testid={testIds.productTour.backButton}
                >
                  Back
                </Button>
              ) : null}
              {isLast ? (
                <>
                  <Button variant="outline" size="default" asChild>
                    <a
                      href={getPublicHelpUrl('web')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gap-1.5"
                      data-testid={testIds.productTour.gettingStartedLink}
                    >
                      Getting started
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                  <Button
                    type="button"
                    size="default"
                    onClick={finishTour}
                    ref={nextButtonRef}
                    data-testid={testIds.productTour.doneButton}
                  >
                    Done
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  size="default"
                  onClick={goNext}
                  ref={nextButtonRef}
                  data-testid={testIds.productTour.nextButton}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null

  return (
    <>
      <Dialog open={promptOpen && !tourActive} onOpenChange={(open) => {
        if (!open) skipPrompt()
      }}>
        <DialogContent
          className="sm:max-w-md"
          showCloseButton={false}
          data-testid={testIds.productTour.prompt}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="h-4 w-4" />
              Take a quick tour?
            </DialogTitle>
            <DialogDescription>
              A two-minute walkthrough of the icon bar, sidebar, main panel, and agent.
              You can replay anytime from Help (?).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={skipPrompt}
              data-testid={testIds.productTour.promptSkip}
            >
              Skip
            </Button>
            <Button
              type="button"
              onClick={startTour}
              data-testid={testIds.productTour.promptStart}
            >
              Take a tour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {overlay}
    </>
  )
}
